import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import classes from './Home9W.module.scss';
import webDevImg from './web-dev.png';
import appDevImg from './app-dev.png';
import workspaceImg from './bg-workspace.png';

gsap.registerPlugin(ScrollTrigger);

const Home9W = () => {
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const heroContentRef = useRef(null);
  const videoRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const ctx = gsap.context(() => {
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      heroTl
        .from(`.${classes.kicker}`, { y: 24, opacity: 0, duration: 0.8, delay: 0.2 })
        .from(`.${classes.heroLine}`, { yPercent: 110, opacity: 0, duration: 1.1, stagger: 0.15 }, '-=0.45')
        .from(`.${classes.subtitle}`, { y: 30, opacity: 0, duration: 0.9 }, '-=0.7')
        .from(`.${classes.tag}`, { y: 20, opacity: 0, duration: 0.7, stagger: 0.1 }, '-=0.55')
        .from(`.${classes.scrollIndicator}`, { opacity: 0, duration: 0.8 }, '-=0.2');

      // Stronger global parallax for video and background layers.
      gsap.to(videoRef.current, {
        scale: 1.28,
        yPercent: 14,
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
        },
      });

      gsap.to(`.${classes.blobA}`, {
        yPercent: -35,
        xPercent: 8,
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
        },
      });

      gsap.to(`.${classes.blobB}`, {
        yPercent: 45,
        xPercent: -12,
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
        },
      });

      gsap.to(`.${classes.gridLayer}`, {
        yPercent: 25,
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
        },
      });

      gsap.to(heroContentRef.current, {
        yPercent: 45,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });

      cardRefs.current.filter(Boolean).forEach((card, index) => {
        gsap.from(card, {
          y: 90,
          opacity: 0,
          duration: 1.1,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 82%',
            toggleActions: 'play none none reverse',
          },
          delay: index * 0.05,
        });

        const image = card.querySelector('img');
        if (image) {
          gsap.fromTo(
            image,
            { scale: 1.18, yPercent: -10 },
            {
              scale: 1.02,
              yPercent: 10,
              ease: 'none',
              scrollTrigger: {
                trigger: card,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
              },
            }
          );
        }
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className={classes.root} ref={rootRef}>
      <div className={classes.fixedBackground}>
        <video
          ref={videoRef}
          className={classes.bgVideo}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={workspaceImg}
        >
          <source src="/react-assets/home9-w-bg.mp4" type="video/mp4" />
        </video>
        <div className={classes.videoOverlay}></div>
        <div className={`${classes.blob} ${classes.blobA}`}></div>
        <div className={`${classes.blob} ${classes.blobB}`}></div>
        <div className={classes.gridLayer}></div>
      </div>

      <section className={classes.hero} ref={heroRef}>
        <div className={classes.heroContent} ref={heroContentRef}>
          <img src="/react-assets/logo.svg" alt="collabo Inc." className={classes.logoImg} />
          <p className={classes.kicker}>collabo digital studio / light edition</p>

          <h1 className={classes.heroTitle}>
            <span className={classes.heroLine}>Bright</span>
            <span className={classes.heroLine}>Flow</span>
            <span className={classes.heroLine}>By Software</span>
          </h1>

          <p className={classes.subtitle}>
            社内業務と顧客接点をつなぎ、
            <br />
            明るく見通しのよい運用体験を届けます。
          </p>

          <div className={classes.tags}>
            <span className={classes.tag}>業務管理</span>
            <span className={classes.tag}>顧客管理</span>
            <span className={classes.tag}>アプリ開発</span>
            <span className={classes.tag}>AI自動化</span>
          </div>
        </div>

        <div className={classes.scrollIndicator}>
          <span className={classes.scrollText}>SCROLL</span>
          <div className={classes.scrollLine}></div>
        </div>
      </section>

      <section className={classes.contentSection}>
        <div className={classes.introBlock}>
          <h2 className={classes.sectionTitle}>HOME9-W / LIGHT MOTION + PARALLAX</h2>
          <p className={classes.sectionLead}>
            白基調の背景動画と多層視差で軽やかな世界観を作りながら、
            実際の提供価値が伝わる情報設計にしています。
          </p>
        </div>

        <article className={classes.serviceCard} ref={(el) => { cardRefs.current[0] = el; }}>
          <div className={classes.serviceText}>
            <p className={classes.serviceType}>Service 01 / Business System</p>
            <h3 className={classes.serviceTitle}>業務管理・顧客管理システム開発</h3>
            <p className={classes.serviceDesc}>
              業務フローを整理し、現場が継続運用しやすいシステムを構築します。
              権限設計、通知、入力導線まで、自社向けに細かく調整します。
            </p>
          </div>
          <div className={classes.serviceImageWrap}>
            <img src={webDevImg} alt="業務管理・顧客管理システム開発" className={classes.serviceImage} />
          </div>
        </article>

        <article className={`${classes.serviceCard} ${classes.reverse}`} ref={(el) => { cardRefs.current[1] = el; }}>
          <div className={classes.serviceText}>
            <p className={classes.serviceType}>Service 02 / App Solution</p>
            <h3 className={classes.serviceTitle}>アプリをつくる。育てる。届ける。</h3>
            <p className={classes.serviceDesc}>
              企画から公開後の改善まで一気通貫で対応します。
              社内利用アプリも顧客向けアプリも、運用に合わせて提供します。
            </p>
          </div>
          <div className={classes.serviceImageWrap}>
            <img src={appDevImg} alt="アプリ開発" className={classes.serviceImage} />
          </div>
        </article>

        <article className={classes.serviceCard} ref={(el) => { cardRefs.current[2] = el; }}>
          <div className={classes.serviceText}>
            <p className={classes.serviceType}>Service 03 / AI + Customization</p>
            <h3 className={classes.serviceTitle}>AI導入と運用カスタマイズ</h3>
            <p className={classes.serviceDesc}>
              問い合わせ対応やデータ整理などの定型業務をAIで自動化します。
              補助金を活用した開発相談にも対応しています
              （採択を保証するものではありません）。
            </p>
          </div>
          <div className={classes.serviceImageWrap}>
            <img src={workspaceImg} alt="AI導入と運用カスタマイズ" className={classes.serviceImage} />
          </div>
        </article>
      </section>

      <footer className={classes.footer}>
        <img src="/react-assets/logo.svg" alt="collabo Inc." className={classes.footerLogo} />
        <p className={classes.footerText}>© {new Date().getFullYear()} collabo Inc. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Home9W;
